import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Badge, Alert, Spinner, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { ReduxState } from "src/redux";
import { Integration, Invoice2 } from "src/docuclipper/DocuclipperTypes";
import { fetchIntegrations } from "src/redux/reducers/Integration";
import {
  fetchTagLevelIntegration,
  linkIntegration,
  unlinkIntegration,
  SUPPORTED_SCOPES,
} from "src/redux/reducers/TagLevelIntegration";
import { updateInvoiceField } from "src/redux/reducers/InvoiceManager";
import { fetchTagLevels } from "src/redux/reducers/TagLevel";
import {
  goToQuickbooksFn,
  goToXeroFn,
} from "../Integrations/IntegrationsTable";
import quickbooksIcon from "src/icons/qb-logo-horizontal-preferred.svg";
import xeroIcon from "src/icons/xero.png";
import { setDownloadInvoiceModalOpen } from "src/redux/reducers/JobExport";
import { setSelectedExportType } from "src/redux/reducers/DownloadInvoiceOptions";

// Integration sync metadata from TransactionLine.metadata.integrations
interface IntegrationSyncInfo {
  id?: number | string;
  type?: string;
  url?: string;
  syncedAt?: string;
  error?: {
    message: string;
    code?: string;
    provider?: string;
    occurredAt?: string;
    details?: Record<string, any>;
  };
}

// Metadata now uses integration ID as key instead of "quickbooks"/"xero"
interface IntegrationsMetadata {
  [integrationId: string]: IntegrationSyncInfo;
}

interface Props {
  invoice: Invoice2 | null;
  transactionLineId?: number;
  transactionLineMetadata?: string | null;
  onIntegrationChange?: (hasIntegration: boolean) => void;
}

interface IntegrationOption {
  value: number;
  label: string;
  integration: Integration;
}

type SyncStatus = "not_synced" | "synced" | "error" | "missing_fields";

// Helper function to format error messages for better user experience
const formatSyncErrorMessage = (errorMessage: string): string => {
  if (!errorMessage) return errorMessage;

  // Check if error is about missing AccountBasedExpenseLineDetail (missing category)
  // This error occurs when a line item is missing a category/account in QuickBooks
  if (
    errorMessage.includes("AccountBasedExpenseLineDetail") ||
    errorMessage.includes("AccountBasedExpenseLineDetail is missing") ||
    errorMessage.includes(
      "Required parameter Line.AccountBasedExpenseLineDetail"
    ) ||
    errorMessage.includes("Required param missing") ||
    errorMessage.includes("need to supply the required value")
  ) {
    return "Category is missing in one or more line items. Please add a category to all line items before syncing.";
  }
  return errorMessage;
};

// Helper function to check if error is about missing category
const isMissingCategoryError = (error: any): boolean => {
  if (!error) return false;

  const mainMessage = error.message || "";
  const nestedMessage = error.details?.fault?.Error?.Message || "";
  const nestedDetail = error.details?.fault?.Error?.Detail || "";

  const allMessages = [mainMessage, nestedMessage, nestedDetail]
    .join(" ")
    .toLowerCase();

  return (
    allMessages.includes("accountbasedexpenselinedetail") ||
    allMessages.includes(
      "required parameter line.accountbasedexpenselinedetail"
    ) ||
    (allMessages.includes("required param missing") &&
      allMessages.includes("accountbasedexpenselinedetail"))
  );
};

export const InvoiceIntegrationSection: React.FC<Props> = ({
  invoice,
  transactionLineId,
  transactionLineMetadata,
  onIntegrationChange,
}) => {
  const dispatch = useDispatch();
  const [showConnectModal, setShowConnectModal] = useState(false);

  // Redux state
  const { integrations, loading: integrationsLoading } = useSelector(
    (state: ReduxState) => state.Integration
  );
  const {
    selectedL1,
    selectedL2,
    tagLevels,
    loading: tagLevelsLoading,
  } = useSelector((state: ReduxState) => state.TagLevel);
  const {
    linkedIntegration,
    linkedIntegrationDetails,
    loading,
    saving,
    error,
  } = useSelector((state: ReduxState) => state.TagLevelIntegration);

  // Parse transaction line metadata to get integration sync info
  const parsedMetadata = React.useMemo(() => {
    if (!transactionLineMetadata) return null;
    try {
      const metadata = JSON.parse(transactionLineMetadata);
      return metadata?.integrations as IntegrationsMetadata | undefined;
    } catch {
      return null;
    }
  }, [transactionLineMetadata]);

  // Get the current integration sync info using integration ID as key
  const integrationSyncInfo = React.useMemo((): IntegrationSyncInfo | null => {
    if (!parsedMetadata || !linkedIntegration) return null;

    // Use integration ID as the key to look up sync info
    const integrationId = linkedIntegration.integrationId;
    if (integrationId) {
      // Try both string and number keys since metadata keys might be either
      const idString = String(integrationId);
      const idNumber = Number(integrationId);

      // First try string key
      if (parsedMetadata[idString]) {
        return parsedMetadata[idString];
      }
      // Then try number key
      if (parsedMetadata[idNumber]) {
        return parsedMetadata[idNumber];
      }
      // Also try the raw integrationId in case it's already the right type
      if (parsedMetadata[integrationId as any]) {
        return parsedMetadata[integrationId as any];
      }
    }
    return null;
  }, [parsedMetadata, linkedIntegration]);

  // Get current tagLevelId
  const currentTagLevelId =
    selectedL1 && selectedL2
      ? (tagLevels?.root as any)?.[selectedL1]?.[selectedL2]?.metadata?.tagId
      : null;

  // Filter to only supported integrations (QuickBooks for now)
  const supportedIntegrations = React.useMemo(
    () =>
      integrations.filter((i) =>
        Object.values(SUPPORTED_SCOPES).includes(i.scope)
      ),
    [integrations]
  );

  // Fetch integrations and tag levels on mount
  useEffect(() => {
    dispatch(fetchIntegrations());
    dispatch(fetchTagLevels());
  }, [dispatch]);

  // Fetch tag level integration when tag level or integrations change.
  // Re-run when integrations load so we can resolve the link (e.g. first load had empty integrations).
  useEffect(() => {
    if (!integrationsLoading && !tagLevelsLoading && currentTagLevelId) {
      dispatch(fetchTagLevelIntegration());
    }
  }, [
    dispatch,
    integrationsLoading,
    tagLevelsLoading,
    currentTagLevelId,
    selectedL1,
    selectedL2,
    integrations.length,
  ]);

  // Notify parent of integration status and set export type
  useEffect(() => {
    const hasIntegration = !!linkedIntegration;
    onIntegrationChange?.(hasIntegration);

    if (
      hasIntegration &&
      (linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.quickbooks ||
        linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero)
    ) {
      dispatch(setSelectedExportType("qbo"));
    } else {
      dispatch(setSelectedExportType("xlsx"));
    }
  }, [
    linkedIntegration,
    linkedIntegrationDetails,
    onIntegrationChange,
    dispatch,
  ]);

  // Get the integration type icon and label
  const getIntegrationInfo = (integration: Integration) => {
    const extraParams = JSON.parse(integration.extraParams || "{}");

    if (integration.scope === SUPPORTED_SCOPES.quickbooks) {
      return {
        icon: quickbooksIcon,
        iconStyle: { height: "24px" },
        type: "QuickBooks",
        label: extraParams.companyName || integration.accountId,
      };
    }
    if (integration.scope === SUPPORTED_SCOPES.xero) {
      return {
        icon: xeroIcon,
        iconStyle: { height: "24px" },
        type: "Xero",
        label:
          extraParams.organisationName ||
          extraParams.tenantName ||
          integration.accountId,
      };
    }
    return {
      icon: null,
      iconStyle: {},
      type: "Unknown",
      label: integration.accountId,
    };
  };

  // Get the integration type name for display
  const getIntegrationTypeName = (): string => {
    if (linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero) {
      return "Xero";
    }
    return "QuickBooks";
  };

  // Get sync status for the invoice - memoized to react to all dependency changes
  const syncStatusResult = React.useMemo((): {
    status: SyncStatus;
    message: string;
    url?: string;
    syncedAt?: string;
    error?: IntegrationSyncInfo["error"];
  } => {
    if (!linkedIntegration || !invoice) {
      return { status: "not_synced", message: "Not connected" };
    }

    const integrationName =
      linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.xero
        ? "Xero"
        : linkedIntegrationDetails?.scope === SUPPORTED_SCOPES.quickbooks
        ? "QuickBooks"
        : "";

    // Check for sync info from metadata
    if (integrationSyncInfo) {
      // If there's an error, show error status (check this FIRST, even if syncedAt exists)
      // Error takes precedence over synced status
      if (integrationSyncInfo.error && integrationSyncInfo.error.message) {
        return {
          status: "error",
          message: integrationSyncInfo.error.message || "Sync failed",
          error: integrationSyncInfo.error,
          url: integrationSyncInfo.url,
          syncedAt: integrationSyncInfo.syncedAt,
        };
      }

      // If synced successfully (has id and/or syncedAt) and NO error
      if (
        (integrationSyncInfo.syncedAt || integrationSyncInfo.id) &&
        !integrationSyncInfo.error
      ) {
        return {
          status: "synced",
          message: `Synced to ${integrationName}`,
          url: integrationSyncInfo.url,
          syncedAt: integrationSyncInfo.syncedAt,
        };
      }
    }

    // Fallback: Check if invoice has been synced - use link or status field
    if (invoice.link && invoice.link.length > 0) {
      return { status: "synced", message: `Synced to ${integrationName}` };
    }

    // Check status field
    if (invoice.status === "synced" || invoice.status === "imported") {
      return { status: "synced", message: `Synced to ${integrationName}` };
    }

    // Check for missing required fields
    const missingFields: string[] = [];
    if (!invoice.vendor && !invoice.customer) {
      missingFields.push("Vendor/Customer");
    }
    if (!invoice.date) {
      missingFields.push("Date");
    }

    if (missingFields.length > 0) {
      return {
        status: "missing_fields",
        message: `Missing: ${missingFields.join(", ")}`,
      };
    }

    return { status: "not_synced", message: "Ready to sync" };
  }, [
    linkedIntegration,
    linkedIntegrationDetails,
    invoice,
    integrationSyncInfo,
  ]);

  const handleLinkIntegration = async (
    option: IntegrationOption | null | undefined
  ) => {
    if (!option || !currentTagLevelId) return;
    const success = Boolean(
      await dispatch(linkIntegration(option.value, currentTagLevelId))
    );

    // If the invoice is currently using Excel docTypes, normalize to integration types
    if (success && invoice) {
      if (invoice.docType === "payables") {
        dispatch(
          updateInvoiceField({ invoice, field: "docType", value: "bill" })
        );
      } else if (invoice.docType === "receivables") {
        dispatch(
          updateInvoiceField({ invoice, field: "docType", value: "invoice" })
        );
      }
    }
    // Modal will close when linkedIntegration is set
    setShowConnectModal(false);
  };

  const handleUnlinkIntegration = async () => {
    if (
      window.confirm("Are you sure you want to disconnect this integration?")
    ) {
      await dispatch(unlinkIntegration());
    }
  };

  const handleConnectNewQuickBooks = () => {
    const next = `&next=${encodeURIComponent(
      window.location.pathname + window.location.search
    )}`;
    goToQuickbooksFn(next, currentTagLevelId || undefined);
  };

  const handleConnectNewXero = () => {
    const next = `&next=${encodeURIComponent(
      window.location.pathname + window.location.search
    )}`;
    goToXeroFn(next, currentTagLevelId || undefined);
  };

  // Build options for Select dropdown
  const options: IntegrationOption[] = supportedIntegrations
    .filter(
      (i) =>
        !linkedIntegration ||
        String(i.id) !== String(linkedIntegration.integrationId)
    )
    .map((integration) => {
      const info = getIntegrationInfo(integration);
      return {
        value: parseInt(integration.id),
        label: info.label,
        integration,
      };
    });

  const formatOptionLabel = (option: IntegrationOption) => {
    const info = getIntegrationInfo(option.integration);
    return (
      <div className="d-flex align-items-center">
        {info.icon && (
          <img src={info.icon} alt={info.type} style={info.iconStyle} />
        )}
        <span className="ml-2">{info.label}</span>
      </div>
    );
  };

  const syncStatus = syncStatusResult;

  const renderSyncBadge = () => {
    switch (syncStatus.status) {
      case "synced":
        return (
          <Badge variant="success">
            <FontAwesomeIcon icon="check" className="mr-1" />
            Synced
          </Badge>
        );
      case "error":
        return (
          <Badge variant="danger">
            <FontAwesomeIcon icon="exclamation-triangle" className="mr-1" />
            Error
          </Badge>
        );
      case "missing_fields":
        return (
          <Badge variant="warning">
            <FontAwesomeIcon icon="exclamation-circle" className="mr-1" />
            Missing Fields
          </Badge>
        );
      default:
        return <Badge variant="secondary">Not Synced</Badge>;
    }
  };

  const integrationInfo = linkedIntegrationDetails
    ? getIntegrationInfo(linkedIntegrationDetails)
    : null;

  // If no project selected, show message
  if (!currentTagLevelId) {
    return (
      <div>
        {/* <h6 className="font-weight-bold mb-3">
          <FontAwesomeIcon icon="cloud-upload-alt" className="mr-2" />
          Export & Sync
        </h6> */}
        <Alert variant="secondary" className="py-2">
          <small>Select a project to manage integrations</small>
        </Alert>
        <Button
          variant="link"
          size="sm"
          className="btn--cta w-100 p-0 text-left"
          onClick={() => dispatch(setDownloadInvoiceModalOpen(true))}
        >
          <FontAwesomeIcon icon="file-excel" className="mr-2" />
          Export to Excel
        </Button>
      </div>
    );
  }

  // Loading state
  if (loading || integrationsLoading || tagLevelsLoading) {
    return (
      <div>
        {/* <h6 className="font-weight-bold mb-3">
          <FontAwesomeIcon icon="cloud-upload-alt" className="mr-2" />
          Export & Sync
        </h6> */}
        <div className="text-center py-3">
          <Spinner animation="border" size="sm" />
          <div className="text-muted small mt-2">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* <h6 className="font-weight-bold mb-3">
        <FontAwesomeIcon icon="cloud-upload-alt" className="mr-2" />
        Export & Sync
      </h6> */}

      {error && (
        <Alert
          variant="danger"
          className="py-2 mb-2"
          dismissible
          onClose={() =>
            dispatch({ type: "TagLevelIntegration/setError", payload: null })
          }
        >
          <small>{error}</small>
        </Alert>
      )}

      {linkedIntegration && integrationInfo ? (
        // Connected state
        <div className="mb-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="d-flex align-items-center">
              {integrationInfo.icon && (
                <img
                  src={integrationInfo.icon}
                  alt={integrationInfo.type}
                  style={integrationInfo.iconStyle}
                />
              )}
              <div className="ml-2">
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "0.85rem" }}
                >
                  {integrationInfo.label}
                </div>
                <small className="text-muted">{integrationInfo.type}</small>
              </div>
            </div>
            <Button
              variant="link"
              size="sm"
              className="text-danger p-0"
              onClick={handleUnlinkIntegration}
              disabled={saving}
            >
              <FontAwesomeIcon icon="times" />
            </Button>
          </div>

          {/* Sync status */}
          <div className="mb-3">
            <small className="text-muted d-block mb-1">Status:</small>
            {renderSyncBadge()}

            {/* Show link to synced invoice */}
            {syncStatus.status === "synced" && syncStatus.url && (
              <div className="mt-2">
                <a
                  href={syncStatus.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-success w-100"
                >
                  <FontAwesomeIcon icon="external-link-alt" className="mr-1" />
                  View in {getIntegrationTypeName()}
                </a>
                {syncStatus.syncedAt && (
                  <small className="text-muted d-block mt-1">
                    Synced: {new Date(syncStatus.syncedAt).toLocaleString()}
                  </small>
                )}
              </div>
            )}

            {/* Show error details */}
            {syncStatus.status === "error" && syncStatus.error && (
              <Alert variant="danger" className="mt-2 py-2 mb-0">
                <small>
                  <FontAwesomeIcon
                    icon="exclamation-triangle"
                    className="mr-1"
                  />
                  <strong>Sync Error: </strong>
                  {(() => {
                    // If this is a missing category error, show the user-friendly message
                    if (isMissingCategoryError(syncStatus.error)) {
                      return "Category is missing in one or more line items. Please add a category to all line items before syncing.";
                    }

                    // Otherwise, prefer nested error message if available, otherwise use main message
                    const nestedMessage =
                      syncStatus.error.details?.fault?.Error?.Message;
                    const mainMessage = syncStatus.error.message;
                    const displayMessage = nestedMessage || mainMessage;
                    return formatSyncErrorMessage(displayMessage);
                  })()}
                </small>
                {/* Show nested error detail only if it's not a missing category error */}
                {syncStatus.error.details?.fault?.Error?.Detail &&
                  !isMissingCategoryError(syncStatus.error) && (
                    <small className="d-block text-muted mt-1">
                      {syncStatus.error.details.fault.Error.Detail}
                    </small>
                  )}
                {syncStatus.error.occurredAt && (
                  <small className="d-block text-muted mt-1">
                    {new Date(syncStatus.error.occurredAt).toLocaleString()}
                  </small>
                )}
                {syncStatus.url && syncStatus.url.length > 0 && (
                  <div className="mt-2">
                    <a
                      href={syncStatus.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                      style={{ fontSize: "0.8rem" }}
                    >
                      <FontAwesomeIcon
                        icon="external-link-alt"
                        className="mr-1"
                      />
                      View last sync
                    </a>
                  </div>
                )}
              </Alert>
            )}

            {/* Show message for other statuses */}
            {syncStatus.message &&
              syncStatus.status !== "synced" &&
              syncStatus.status !== "error" && (
                <small className="text-muted d-block mt-1">
                  {syncStatus.message}
                </small>
              )}
          </div>

          {/* Secondary CTA - Export Excel */}
          <Button
            variant="link"
            size="sm"
            className="btn--cta w-100 p-0 text-left"
            onClick={() => dispatch(setDownloadInvoiceModalOpen(true))}
          >
            <FontAwesomeIcon icon="file-excel" className="mr-2" />
            Export to Excel
          </Button>
        </div>
      ) : (
        // Not connected state
        <div>
          <Alert variant="secondary" className="py-2 mb-2">
            <small>No integration connected to this project</small>
          </Alert>

          <Button
            variant="outline-primary"
            size="sm"
            className="w-100 mb-2"
            onClick={() => setShowConnectModal(true)}
          >
            <FontAwesomeIcon icon="plug" className="mr-2" />
            Connect Integration
          </Button>

          {/* Primary CTA - Export Excel when no integration */}
          <Button
            variant="link"
            size="sm"
            className="btn--cta w-100 p-0 text-left"
            onClick={() => dispatch(setDownloadInvoiceModalOpen(true))}
          >
            <FontAwesomeIcon icon="file-excel" className="mr-2" />
            Export to Excel
          </Button>
        </div>
      )}

      {/* Connect Integration Modal */}
      <Modal show={showConnectModal} onHide={() => setShowConnectModal(false)}>
        {/* @ts-ignore - react-bootstrap type issue */}
        <Modal.Header closeButton>
          <Modal.Title>Connect Integration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Connect a QuickBooks or Xero account to this project for direct
            invoice syncing.
          </p>

          {options.length > 0 ? (
            <>
              <Form.Group className="mb-3">
                <Form.Label className="font-weight-bold">
                  Select Existing Account
                </Form.Label>
                <Select
                  options={options}
                  formatOptionLabel={formatOptionLabel}
                  placeholder="Choose an account..."
                  isClearable
                  isDisabled={saving}
                  onChange={handleLinkIntegration}
                  value={null}
                />
              </Form.Group>

              <div className="text-center text-muted my-3">
                <span>or connect a new account</span>
              </div>
            </>
          ) : (
            <Alert variant="secondary" className="mb-3">
              <small>No QuickBooks or Xero accounts connected yet.</small>
            </Alert>
          )}

          <div
            className="d-flex justify-content-center align-items-center"
            style={{ gap: "2rem" }}
          >
            {/* QuickBooks Connect */}
            <div className="text-center">
              <Button
                variant="outline-primary"
                onClick={handleConnectNewQuickBooks}
                className="d-flex align-items-center"
                style={{ padding: "6px 16px" }}
              >
                <img
                  src={quickbooksIcon}
                  alt="Connect to QuickBooks"
                  style={{ height: "24px", marginRight: "8px" }}
                />
                Connect QuickBooks
              </Button>
              <br />
              <small className="text-muted">Connect QuickBooks</small>
            </div>

            {/* Xero Connect */}
            <div className="text-center">
              <Button
                variant="outline-primary"
                onClick={handleConnectNewXero}
                className="d-flex align-items-center"
                style={{ padding: "6px 16px" }}
              >
                <img
                  src={xeroIcon}
                  alt="Xero"
                  style={{ height: "24px", marginRight: "8px" }}
                />
                Connect Xero
              </Button>
              <br />
              <small className="text-muted">Connect Xero</small>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConnectModal(false)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InvoiceIntegrationSection;
